// MPC1000 / JJOS .PGM parser — see FORMAT.md for the byte-level spec.
import { detectPgmProfile } from './os-profile.js';
import { attachProgramNoteResolver } from './program-note-resolution.js';

const HEADER_SIZE = 0x18;
const PAD_RECORD_SIZE = 0xA4;
const SAMPLE_SLOT_SIZE = 0x18;
const PAD_COUNT = 64;
const SAMPLES_PER_PAD = 4;
const NOTE_TO_PAD_OFFSET = 0x2958;
const NOTE_TO_PAD_SIZE = 128;
const MIN_PGM_SIZE = NOTE_TO_PAD_OFFSET + NOTE_TO_PAD_SIZE;

function readAsciiZ(view, offset, length) {
  const bytes = new Uint8Array(view.buffer, view.byteOffset + offset, length);
  let end = bytes.indexOf(0);
  if (end === -1) end = length;
  return new TextDecoder('ascii').decode(bytes.subarray(0, end)).trim();
}

export function parsePGM(arrayBuffer, name = '') {
  if (arrayBuffer.byteLength < HEADER_SIZE) {
    throw new Error('Unsupported or corrupt .PGM file: header is too short.');
  }
  const view = new DataView(arrayBuffer);
  // u32 (see FORMAT.md / all.js — every file/blob in this format opens with
  // a self-length field, consistently 32-bit). Doesn't change behavior here
  // since PGM is always exactly 10,756 bytes (well under the u16 wrap
  // point), but kept consistent with the confirmed convention.
  const fileSize = view.getUint32(0x00, true);
  const version = readAsciiZ(view, 0x04, 16);
  if (!/^MPC1000 PGM\b/.test(version)) {
    const header = readAsciiZ(view, 0x00, Math.min(16, arrayBuffer.byteLength));
    const label = header || version || 'unknown';
    throw new Error(`Unsupported .PGM header "${label}". This maker currently supports MPC1000 stock/JJOS .PGM files.`);
  }
  if (arrayBuffer.byteLength < MIN_PGM_SIZE) {
    throw new Error('Unsupported or corrupt .PGM file: required note-map table is missing.');
  }
  const profile = detectPgmProfile(version);

  const pads = [];
  for (let p = 0; p < PAD_COUNT; p++) {
    const padOff = HEADER_SIZE + p * PAD_RECORD_SIZE;

    const samples = [];
    for (let s = 0; s < SAMPLES_PER_PAD; s++) {
      const sOff = padOff + s * SAMPLE_SLOT_SIZE;
      const sampleName = readAsciiZ(view, sOff, 16);
      if (!sampleName) continue;
      samples.push({
        name: sampleName,
        level: view.getUint8(sOff + 0x11),
        rangeLower: view.getUint8(sOff + 0x12),
        rangeUpper: view.getUint8(sOff + 0x13),
        tuning: view.getInt16(sOff + 0x14, true),
        playMode: view.getUint8(sOff + 0x16), // 0 = One Shot, 1 = Note On
        // PGM 6.00 / OS3 stores a 1-based NDC slice number in the last byte
        // of the sample slot. Older confirmed chop projects use the
        // pad-level byte at +0x5c instead; the post-pass below accepts
        // either only when a repeated-sample group forms a complete run.
        chopIndex1Based: view.getUint8(sOff + 0x17),
      });
    }

    pads.push({
      index: p,
      samples,
      voiceOverlap: view.getUint8(padOff + 0x62),
      muteGroup: view.getUint8(padOff + 0x63),
      attack: view.getUint8(padOff + 0x66),
      decay: view.getUint8(padOff + 0x67),
      decayMode: view.getUint8(padOff + 0x68),
      // Confirmed against real hardware (see FORMAT.md): JJOS's Sustain/
      // Release pair, sitting right between decayMode and velocityToLevel.
      // Untouched-default is Sustain=100/Release=20 on virtually every real
      // pad; a pad manually set to a longer Release (e.g. 100) reads back
      // exactly that value here.
      sustain: view.getUint8(padOff + 0x69),
      release: view.getUint8(padOff + 0x6a),
      velocityToLevel: view.getUint8(padOff + 0x6b),
      filter1Type: view.getInt8(padOff + 0x71),
      filter1Freq: view.getUint8(padOff + 0x72),
      filter1Res: view.getUint8(padOff + 0x73),
      filter1VelToFreq: view.getUint8(padOff + 0x78),
      filter2Type: view.getInt8(padOff + 0x79),
      filter2Freq: view.getUint8(padOff + 0x7a),
      filter2Res: view.getUint8(padOff + 0x7b),
      filter2VelToFreq: view.getUint8(padOff + 0x80),
      mixerLevel: view.getUint8(padOff + 0x8f),
      mixerPan: view.getUint8(padOff + 0x90),
      output: view.getInt8(padOff + 0x91),
      fxSend: view.getInt8(padOff + 0x92),
      fxSendLevel: view.getUint8(padOff + 0x93),
      filterAttenuation: view.getUint8(padOff + 0x94),
      // JJOS 8.x NDC programs can keep the source Chop1-Chop32 number here.
      // In ordinary sequential chop families this is just chopIndex+1; in
      // repad/reordered families it can point to a different stored WAV smpl
      // window than the pad-order chopIndex at +0x5c.
      storedChopIndex1Based: view.getUint8(padOff + 0x96),
      // 0-indexed chop-slice number — see the chopTotalSlices comment below
      // for what this actually means and how it was confirmed.
      chopIndex: view.getUint8(padOff + 0x5c),
    });
  }

  // Chop-family detection: confirmed against two real projects
  // (`DEC 20 COO`/Program07: 16 pads all naming the identical sample
  // "Sample02" with byte-identical level/tuning, differing only in
  // chopIndex running 0-15; `July 25`/Program03: same pattern, 2 pads,
  // indices 0-1) that when multiple pads in one program share an exact
  // sample name with a chopIndex forming a complete 0..N-1 run, each pad
  // is meant to play chop `chopIndex` of `chopTotalSlices`, not the whole
  // file. Exact OS2XL/OS3 chop start/end windows can live in the WAV's
  // multi-loop smpl table (see readWavSamplerInfo in audio-engine.js);
  // when that table is absent, playback falls back to equal division. The
  // total slice count is only recoverable by looking across every pad that
  // references the same sample name in this program, hence the whole-PGM
  // post-pass rather than a per-pad read.
  //
  // chopIndex alone is NOT sufficient — confirmed on a real regression
  // (`NOV 13 VIDEO`/Program01): 12 pads, each a *different*, unrelated
  // one-shot drum sample (kick/snare/hat, all distinct names), yet every
  // one still carries its own nonzero chopIndex (0,1,3,4,5,7...). This
  // field is clearly populated as some kind of general per-program
  // sample-slot counter regardless of chop-ness, and only means anything
  // as a slice index in the specific case where multiple pads share one
  // exact name. Trusting chopIndex on a single-use name was silently
  // truncating ordinary one-shot hits to a random fragment of themselves
  // — audibly, "the pad triggers but no sound comes out" (or a near-
  // silent tail). Guard: only treat a name as a real chop family if its
  // pads' sorted chopIndex values form a complete, gapless run starting
  // at 0 — exactly what both confirmed real chops look like (16/16 and
  // 2/2, no gaps, no repeats) — not just "more than one pad, nonzero
  // index."
  const padsByName = new Map();
  for (const pad of pads) {
    const name = pad.samples[0]?.name;
    if (!name) continue;
    if (!padsByName.has(name)) padsByName.set(name, []);
    padsByName.get(name).push(pad);
  }
  for (const pad of pads) {
    pad.chopTotalSlices = 1;
    const name = pad.samples[0]?.name;
    const group = name ? padsByName.get(name) : null;
    if (!group || group.length < 2) continue;
    const padLevelIndices = group.map((p) => p.chopIndex).sort((a, b) => a - b);
    const isPadLevelRun = padLevelIndices.every((v, i) => v === i);
    if (isPadLevelRun) {
      pad.chopTotalSlices = padLevelIndices.length;
      if (pad.storedChopIndex1Based > 0) pad.storedChopIndex = pad.storedChopIndex1Based - 1;
      continue;
    }

    const sampleSlotIndices = group
      .map((p) => p.samples[0]?.chopIndex1Based ?? 0)
      .sort((a, b) => a - b);
    const isSampleSlotRun = sampleSlotIndices.every((v, i) => v === i + 1);
    if (isSampleSlotRun) {
      pad.chopIndex = (pad.samples[0]?.chopIndex1Based ?? 1) - 1;
      pad.chopTotalSlices = sampleSlotIndices.length;
      if (pad.storedChopIndex1Based > 0) pad.storedChopIndex = pad.storedChopIndex1Based - 1;
    }
  }

  const padToNote = new Uint8Array(arrayBuffer, 0x2918, 64);
  const noteToPad = new Uint8Array(arrayBuffer, 0x2958, 128);

  return attachProgramNoteResolver({
    kind: 'pgm',
    name,
    fileSize,
    version,
    profile,
    pads,
    padToNote: Array.from(padToNote),
    noteToPad: Array.from(noteToPad),
  });
}
